import { RenderTexture, useGLTF, useTexture } from '@react-three/drei';
import { extend } from '@react-three/fiber';
import { MeshLineGeometry, MeshLineMaterial } from 'meshline';
import { type ReactNode, useLayoutEffect, useRef } from 'react';
import * as THREE from 'three';
import { BAND_TEXTURE, TAG_GLB } from './Badge';

extend({ MeshLineGeometry, MeshLineMaterial });

// Where the physics badge comes to rest: three one-unit rope segments hang from the
// anchor at y=4, and the card's clip sits 1.45 below the last of them.
const ROPE_LENGTH = 3;
const CARD_Y = -ROPE_LENGTH - 1.45;

// The badge of the exported social image. Same card, clip and band as <Badge>, but placed
// at its rest pose instead of simulated: a physics badge that is captured after a fixed
// number of frames is still swinging, which is what used to leave the exported
// credential tilted.
export function StaticBadge({ face }: { face: ReactNode }) {
  const band = useRef<THREE.Mesh<MeshLineGeometry, MeshLineMaterial>>(null);
  const { nodes, materials } = useGLTF(TAG_GLB);
  const texture = useTexture(BAND_TEXTURE);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;

  useLayoutEffect(() => {
    band.current?.geometry.setPoints([
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(0, -ROPE_LENGTH, 0),
    ]);
  }, []);

  return (
    <group position={[0, 4, 0]}>
      <group position={[0, CARD_Y, 0]}>
        <group scale={2.25} position={[0, -1.2, -0.05]}>
          {/* @ts-expect-error untyped GLTF nodes */}
          <mesh geometry={nodes.card.geometry}>
            <meshPhysicalMaterial
              clearcoat={1}
              clearcoatRoughness={0.15}
              roughness={0.3}
              metalness={0.5}
            >
              <RenderTexture
                attach="map"
                anisotropy={16}
                width={1024}
                height={1024}
              >
                {face}
              </RenderTexture>
            </meshPhysicalMaterial>
          </mesh>
          <mesh
            // @ts-expect-error untyped GLTF nodes
            geometry={nodes.clip.geometry}
            material={materials.metal}
            material-roughness={0.3}
          />
          {/* @ts-expect-error untyped GLTF nodes */}
          <mesh geometry={nodes.clamp.geometry} material={materials.metal} />
        </group>
      </group>
      <mesh ref={band}>
        {/* @ts-expect-error meshline extended elements */}
        <meshLineGeometry />
        {/* @ts-expect-error meshline extended elements */}
        <meshLineMaterial
          color="white"
          depthTest={false}
          resolution={[1000, 1000]}
          useMap={1}
          map={texture}
          repeat={new THREE.Vector2(3, 1)}
          lineWidth={0.2}
        />
      </mesh>
    </group>
  );
}
